import { ObjectPrototype } from "@/models/objectPrototype.js"
import { Breaker } from "@/models/breaker.js"
import { ElectricMeter } from "@/models/electricMeter.js"
import { PowerUnit } from "@/models/powerUnit.js"
import { ServiceUnit } from "@/models/serviceUnit.js"
import { ModemUnit } from "@/models/modemUnit.js"




// Station blueprint
export class Station extends ObjectPrototype {
	constructor(id) {
		//console.log("Create station id=", id)
		super(id)

		this.power_unit = new PowerUnit(this.passport)
		this.service_unit = new ServiceUnit(this.passport)
		this.electricMeter = new ElectricMeter(this.passport)
		this.modem_unit = new ModemUnit(this.passport)
		this.breaker = new Breaker(this.passport)

		this.specificMetrics = {
			// Service unit metrics
			boxDoorLastOpenTime: undefined,
			boxDoorLastCloseTime: undefined,
			// Breaker metrics
			fBreakerActiveLast: undefined,
			fEepromErrorLast: undefined,
			fServiceUnitConnectionErrorLast: undefined,
			fPowerUnitConnectionErrorLast: undefined,
			fGpsDataOkLast: undefined,
		}
	}

	// Some passport key names (not all)
	static get passportKeys() {
		const proprietaryKeys = {
			// Unit passports from unit objects
			powerUnit: PowerUnit.passportKeys,
			serviceUnit: ServiceUnit.passportKeys,
			electricMeter: ElectricMeter.passportKeys,
			modemUnit: ModemUnit.passportKeys,
			breaker: Breaker.passportKeys,

			// Station passport keys
			//
			ip: 'ip',
			surveyPeriod: 'survey_period',
			surveyStartTime: 'survey_start_time',
			potentialSource: 'potential_source',
			techId: 'tech_id',
			stationType: 'station_type',
			protectiveShelterType: 'protective_shelter_type',
			powerCable: 'power_cable',
			electrodesCount: 'electrodes_count',
			protectionMethod: 'protection_method',
			overhaulDate: 'overhaul_date',
			
			// Stabilization
			stabilization: Object.freeze({
				self: 'stabilization', // self name
				parameter: 'parameter',
				value: 'value',
				actor: 'actor_id', // Object of accounts
				updateTime: 'update_time',
			}),


			measurePotentialOnOffStation:  Object.freeze({
				self: 'measure_potential_on_off_station',
				isEnable: 'is_enable',
				period: 'period',
				startTime: 'start_time',
			}),
			
			autobuildMonthReport: 'autobuild_month_report',

			notificationDistributionLists: {
				self: 'notification_distribution_lists',
				connection: 'connection',
				potentialOutOfRange: 'potential_out_of_range',
				u220: 'u220',
				channel_1: 'channel_1',
				channel_2: 'channel_2',
				shunt: 'shunt',
				securityCircuit1: 'security_circuit_1',
				securityCircuit2: 'security_circuit_2',
				securityCircuit3: 'security_circuit_3',
				securityCircuit4: 'security_circuit_4',
				securityCircuit5: 'security_circuit_5',
				securityCircuit6: 'security_circuit_6',
				// securityCircuit: 'security_circuit_'    // template without number
			},
		}

		return Object.freeze({... ObjectPrototype.passportKeys, ... proprietaryKeys})
	}

	static get analyticMetrics() {
		return Object.freeze({
			failConnectCntr: 'fail_connect_cntr',
			failReadCntr: 'fail_read_cntr',
			successTransactionCntr: 'success_transaction_cntr',
			invalidMessagesReceivedCntr: 'invalid_messages_received_cntr',
			unexpectedlyClosedConnectionCntr: 'unexpectedly_closed_connection_cntr',
		})
	}

	static get notificationNames() {
		const notifications = Station.passportKeys.notificationDistributionLists
		delete notifications.self
	
		return Object.values(notifications)
	}

	get securityCircuitsNotificationNames() {
		const serviceUnitVersion = this.passport.get(ServiceUnit.passportKeys.self)[ServiceUnit.passportKeys.unitVersion]

		if (serviceUnitVersion == ServiceUnit.versions.unknown) {
			return []
		}

		if ([
			ServiceUnit.versions['2022-1'],
			ServiceUnit.versions['2022-2'],
			ServiceUnit.versions['2024-1'],
			ServiceUnit.versions['2024-2'],
		].includes(serviceUnitVersion)) {
			return [
				Station.passportKeys.notificationDistributionLists.securityCircuit1,
				Station.passportKeys.notificationDistributionLists.securityCircuit2,
				Station.passportKeys.notificationDistributionLists.securityCircuit3,
				Station.passportKeys.notificationDistributionLists.securityCircuit4,
				Station.passportKeys.notificationDistributionLists.securityCircuit5,
				Station.passportKeys.notificationDistributionLists.securityCircuit6,
			]
		}

		if ([
			ServiceUnit.versions['2025-1'],
			ServiceUnit.versions['2025-2'],
			ServiceUnit.versions['2025-3'],
		].includes(serviceUnitVersion)) {
			return [
				Station.passportKeys.notificationDistributionLists.securityCircuit1,
				Station.passportKeys.notificationDistributionLists.securityCircuit2,
				Station.passportKeys.notificationDistributionLists.securityCircuit3,
			]
		}

		return []
	}

	static get settingNames() {
		return Object.freeze({
			...ObjectPrototype.settingNames,
			
			[Station.passportKeys.surveyPeriod]: 'Період опитування',
			[Station.passportKeys.surveyStartTime]: 'Час початку опитування',
			[Station.passportKeys.techId]: 'Технічний номер УКЗ, УДЗ',
			[Station.passportKeys.stationType]: 'Тип',
			[Station.passportKeys.protectiveShelterType]: 'Тип захисного укриття УКЗ',
			[Station.passportKeys.potentialSource]: 'Джерело значень потенціалу',
			[Station.passportKeys.powerCable]: 'Кабель живлення тип, переріз',
			[Station.passportKeys.electrodesCount]: 'Кількість електродів/комплектів анодного заземлення',
			[Station.passportKeys.protectionMethod]: 'Спосіб захисту (у разі двох і більше ниток газопроводів)',
			[Station.passportKeys.overhaulDate]: 'Дата капітального ремонту/монтажу анодного заземлення',
			[Station.passportKeys.isActive]: 'Станція активна?',
			[Station.passportKeys.autobuildMonthReport]: 'Автоматично формувати звіт щомісяця',
			[Station.passportKeys.measurePotentialOnOffStation.self]: Object.freeze({
				self: 'Вимірювання потенціалу на вимкненній СКЗ',
				[Station.passportKeys.measurePotentialOnOffStation.isEnable]: 'Періодично вимірювати?',
				[Station.passportKeys.measurePotentialOnOffStation.period]: 'Частота, дні',
				[Station.passportKeys.measurePotentialOnOffStation.startTime]: 'Час початку',
			}),
			[Station.passportKeys.stabilization.self]:  Object.freeze({
				self: 'Задане значення стабілізації',
				[Station.passportKeys.stabilization.parameter]: 'Параметр',
				[Station.passportKeys.stabilization.value]: 'Значення',
			}),
			[ObjectPrototype.passportKeys.notificationDistributionLists.self]: Object.freeze({
				self: 'Сповіщення',
				[ObjectPrototype.passportKeys.notificationDistributionLists.connection]: 'Сповіщати про втрату зв\'язку',
				[ObjectPrototype.passportKeys.notificationDistributionLists.potentialOutOfRange]: 'Потенціал поза цільовим діапазоном',
				[Station.passportKeys.notificationDistributionLists.u220]: 'Втрата живлення 220В',
				[Station.passportKeys.notificationDistributionLists.channel_1]: 'Канал 1',
				[Station.passportKeys.notificationDistributionLists.channel_2]: 'Канал 2',
				[Station.passportKeys.notificationDistributionLists.shunt]: 'Шунт',
				[Station.passportKeys.notificationDistributionLists.securityCircuit1]: 'Контур 1',
				[Station.passportKeys.notificationDistributionLists.securityCircuit2]: 'Контур 2',
				[Station.passportKeys.notificationDistributionLists.securityCircuit3]: 'Контур 3',
				[Station.passportKeys.notificationDistributionLists.securityCircuit4]: 'Контур 4',
				[Station.passportKeys.notificationDistributionLists.securityCircuit5]: 'Контур 5',
				[Station.passportKeys.notificationDistributionLists.securityCircuit6]: 'Контур 6',
			}),

			[PowerUnit.passportKeys.self]: PowerUnit.settingNames,
			...PowerUnit.settingNames,

			[ServiceUnit.passportKeys.self]: ServiceUnit.settingNames,
			...ServiceUnit.settingNames,

			[ElectricMeter.passportKeys.self]: ElectricMeter.settingNames,
			...ElectricMeter.settingNames,

			[ModemUnit.passportKeys.self]: ModemUnit.settingNames,
			...ModemUnit.settingNames,

			[Breaker.passportKeys.self]: Breaker.settingNames,
			...Breaker.settingNames,
		})
    }

	static get unitNames() {
		return Object.freeze({
			[Station.passportKeys.powerUnit.self]: 'Силовий модуль',
			[Station.passportKeys.serviceUnit.self]: 'Сервісний модуль',
			[Station.passportKeys.electricMeter.self]: 'Лічильник електроенергії',
			[Station.passportKeys.modemUnit.self]: 'Модем',
			[Station.passportKeys.breaker.self]: 'Переривач',
		})
	}

	static get types() {
		return Object.freeze({
			inverter: Object.freeze({code: 'inverter', title: 'інверторний'}),
			transformer: Object.freeze({code: 'transformer', title: 'трансформаторний'}),
			drain: Object.freeze({code: 'drain', title: 'дренажний'}),
		})
	}

	// Спосіб захисту (у разі двох і більше ниток газопроводів)
	static get protectionMethods() {
		return Object.freeze({
			shared_unregulated: Object.freeze({
				key: 'shared_unregulated',
				title: 'спільний нерегульований',
			}),
			shared_bsz: Object.freeze({
				key: 'shared_bsz',
				title: 'спільний через БСЗ',
			})
		})
	}

	static get failureMetrics() {
		return [
			...PowerUnit.alertKeys,
			PowerUnit.metricKeys.fBreakCurrent,
			PowerUnit.metricKeys.fBreakPotential,
			ServiceUnit.metricKeys.fExternalPwr,
		]
	}

	static failureTitle(key) {
		const extraPromts = {
			[PowerUnit.metricKeys.fBreakCurrent]: 'обрив навантаження',
			[PowerUnit.metricKeys.fBreakPotential]: 'обрив МСЕП',
			[ServiceUnit.metricKeys.fExternalPwr]: 'обрив мережі живлення',
		}

		return PowerUnit.alertPromt(key) ?? extraPromts[key] ?? 'невідомо'
	}

	get electricMeterIsSeparateUnit() {
		if (!this.passport.has(Station.passportKeys.electricMeter.self)
			|| !this.passport.get(Station.passportKeys.electricMeter.self)[Station.passportKeys.electricMeter.type]) {
			return undefined
		}

		return [ElectricMeter.versions.nikDlms, ElectricMeter.versions.gama100G1m, ElectricMeter.versions.gama100G1b]
			.includes(this.passport.get(Station.passportKeys.electricMeter.self)[Station.passportKeys.electricMeter.type])
	}

	get electricMeterIsServiceUnit() {
		if (!this.passport.has(Station.passportKeys.electricMeter.self)
			|| !this.passport.get(Station.passportKeys.electricMeter.self)[Station.passportKeys.electricMeter.type]) {
			return undefined
		}

		return this.passport.get(Station.passportKeys.electricMeter.self)[Station.passportKeys.electricMeter.type] == ElectricMeter.versions.impulseInput
	}

	getMetric(metricName, unit="any") {
		if (this.power_unit.getMetric(metricName) && 
			(unit == "any" || unit == "power")) {
			return this.power_unit.getMetric(metricName)
		} else if (this.service_unit.getMetric(metricName) && 
			(unit == "any" || unit == "service")) {
			return this.service_unit.getMetric(metricName)
		} else if (this.electricMeter.metrics.has(metricName) && 
			(unit == "any" || unit == "electric meter")) {
			return this.electricMeter.metrics.get(metricName)
		} else if (this.modem_unit.getMetric(metricName) && 
			(unit == "any" || unit == "modem")) {
			return this.modem_unit.getMetric(metricName)
		} else {
			return undefined
		}
	}

	getFormatedMetric(metricName, unit="any", fixed=null) {
		let metric = this.getMetric(metricName, unit)

		if (metric === undefined) {
			return ''
		}

		if (typeof metric == 'object') {
			if (fixed !== null) {
				metric = metric.value.toFixed(fixed)
			} else {
				metric = metric.value
			}
		}

		return metric
	}

	get updateTime() {
		// Returns update time
		let time = undefined

		if (!this.power_unit.getMetric(PowerUnit.metricKeys.isConnect) &&
			!this.service_unit.getMetric(ServiceUnit.metricKeys.isConnect)) {
			return undefined
		} else if (!this.power_unit.getMetric(PowerUnit.metricKeys.isConnect)) {
			time = this.service_unit.getMetric(ServiceUnit.metricKeys.isConnect).time
		} else if (!this.service_unit.getMetric(ServiceUnit.metricKeys.isConnect)) {
			time = this.power_unit.getMetric(PowerUnit.metricKeys.isConnect).time
		} else {
			// Get the last unit read time
			time = (Array(
				this.service_unit.getMetric(ServiceUnit.metricKeys.isConnect).time,
				this.power_unit.getMetric(PowerUnit.metricKeys.isConnect).time
			).sort()
			.reverse())[0]
		}

		return time
	}

	get measureTime() {
		if (!this.power_unit.getMetric(PowerUnit.metricKeys.lastConnection)
			&& !this.service_unit.getMetric(ServiceUnit.metricKeys.lastConnection)) {
			return undefined
		} else if (!this.power_unit.getMetric(PowerUnit.metricKeys.lastConnection)) {
			return this.service_unit.getMetric(ServiceUnit.metricKeys.lastConnection).value
		} else if (!this.service_unit.getMetric(ServiceUnit.metricKeys.lastConnection)) {
			return this.power_unit.getMetric(PowerUnit.metricKeys.lastConnection).value
		} else {
			// Get the last unit read time
			return (Array(
				this.service_unit.getMetric(ServiceUnit.metricKeys.lastConnection).value,
				this.power_unit.getMetric(PowerUnit.metricKeys.lastConnection).value)
				.sort()
				.reverse())[0]
		}
	}

	static get uProtectSources() {
		return Object.freeze({
			none: 'none',
			powerUnit: 'power_unit',
			serviceUnit: 'service_unit',
		})
	}

	get uProtectSource() {
		// If no source is define
		if (!this.passport?.get(Station.passportKeys.potentialSource)) {
			return 'power'
		} else if (this.passport.get(Station.passportKeys.potentialSource) == Station.uProtectSources.none) {
			return null
		} else if (this.passport.get(Station.passportKeys.potentialSource) == Station.uProtectSources.powerUnit) {
			return 'power'
		} else if (this.passport.get(Station.passportKeys.potentialSource) == Station.uProtectSources.serviceUnit) {
			return 'service'
		} else {
			return undefined
		}
	}

	static get potentialSourceNames() {
		return Object.freeze({
			[Station.uProtectSources.powerUnit]: 'Силовий модуль',
			[Station.uProtectSources.serviceUnit]: 'Сервісний модуль',
			[Station.uProtectSources.none]: 'Потенціал не відстежується',
		})
	}

	get eOut() {
		if (this.power_unit.getMetric(PowerUnit.metricKeys.eOut)) {
			return this.power_unit.getMetric(PowerUnit.metricKeys.eOut).value
		}
		else if (this.service_unit.getMetric(ServiceUnit.metricKeys.eAvg)) {
			return this.service_unit.getMetric(ServiceUnit.metricKeys.eAvg).value
		} else {
			return undefined
		}
	}

	get protectionRange() {
		const e = this.eOut

		if (-2.5 <= e && e <= -0.85)
			return "ok"
		else if (-0.85 < e && e <= 50)
			return "no"
		else if (-50 <= e && e < -2.5)
			return "hi"
		else
			return undefined
	}

	get protectionText() {
		// If no potential source is define
		if (this.uProtectSource == null
			|| this.uProtectSource == undefined
		) {
			return 'не відстежується'
		}
		
		// Mapping potential value and generate protection string
		const range = this.protectionRange

		if (range == "ok")
			return "норма"
		else if (range == "no")
			return "відсутній"
		else if (range == "hi")
			return "завищенний"
		else
			return "-"
	}

	get controlTypeText() {
		// Mapping control code, generate mode string
		if (this.power_unit.getMetric("mode_local"))
			if (this.power_unit.getMetric("mode_local").value)
				return "дистанційний"
			else
				return "місцевий"
		else
			return "-"
	}

	static get protectionVariantsText() {
		return ['норма', 'відсутній', 'завищенний', 'не відстежується', '-']
	}

	static get modeVariantsText() {
		return ['струм', 'потенціал', 'напруга', 'mix', 'невідомий', '-']
	}

	static get activityVariantsText() {
		return ['так' , 'ні']
	}

	subscribeToNotification(userId, notificationKey) {
		let notificationDistributionLists = this.passport.get(Station.passportKeys.notificationDistributionLists.self)
		if (!notificationDistributionLists) {
			this.passport.set(Station.passportKeys.notificationDistributionLists.self, new Object())
			notificationDistributionLists = this.passport.get(Station.passportKeys.notificationDistributionLists.self)
		}

		// if no list at all
		if (notificationDistributionLists[notificationKey] === undefined) {
			notificationDistributionLists[notificationKey] = []
		}

		// If current user is not in distribution notifications list
		if (!notificationDistributionLists[notificationKey].includes(userId)) {
			notificationDistributionLists[notificationKey].push(userId)
		}
	}

	unsubscribeFromNotification(userId, notificationKey) {
		let notificationDistributionLists = this.passport.get(Station.passportKeys.notificationDistributionLists.self)
		if (!notificationDistributionLists) {
			this.passport.set(Station.passportKeys.notificationDistributionLists.self, new Object())
			notificationDistributionLists = this.passport.get(Station.passportKeys.notificationDistributionLists.self)
		}

		// If current user is in distribution notifications list
		if (notificationDistributionLists[notificationKey] !== undefined
			&& notificationDistributionLists[notificationKey].includes(userId)
		) {
			const index = notificationDistributionLists[notificationKey].indexOf(userId);
			notificationDistributionLists[notificationKey].splice(index, 1)
		}
	}
}
